import { defineComponent, ref, provide, reactive, toRefs, getCurrentInstance, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import PageLoading from '@/components/common/PageLoading.vue';
import Swiper from '@/components/layout/Swiper.vue';
import SearchBar2 from '@/components/layout/SearchBar2.vue';
import Footer from '@/components/layout/footer.vue';
import bus from 'vue3-eventbus';
import SideComponent from '@/components/common/SideComponent.vue';
import Loading from '@/components/common/Loading.vue';
import NewIndexSiteLayout from '@/components/newIndex/NewIndexSiteLayout.vue';
import { wexinShare } from '@/utils/wxshare.js';
import { buildIntlVideoUrl, buildIntlDetailUrl } from '@/utils/seoUrl.js';
import { SiteInfo } from '@/api/common';
import { getImageSize } from '@/utils/utils';

export default defineComponent({
	components: {
		PageLoading,
		Swiper,
		SearchBar2,
		Footer,
		SideComponent,
		Loading,
		NewIndexSiteLayout,
	},
	setup() {
		const { proxy } = getCurrentInstance();
		const route = useRoute();
		const data = reactive({
			isLoading: true,
			showHeader: true,
			loginStyle: 'min-width:141px;',
			mobileIconStyle: 'color:rgba(223, 58, 8, 1);',
			showListIcon: true,
			picList: [],
			videoList: [],
			videoColumns: [[], [], []],
			picPage: 1,
			videoPage: 1,
			picNomore: true,
			videoNomore: true,
			isPicLoading: true,
			isVideoLoading: false,
			isMobile: false,
			international_sliders: [],
			pictype: 1,
			videotype: 2,
		});

		const showMenu = ref(proxy.$menus);
		provide('showMenu', showMenu);

		const store = useStore();

		const getSiteInfo = () => {
			return new Promise((resolve, reject) => {
				setTimeout(() => {
					store.dispatch('common/fetchSiteInfo').then(async (res) => {
						let sliders = res.international_sliders || [];
						if (!sliders.length) {
							try {
								const fresh = await SiteInfo();
								sliders = fresh.international_sliders || [];
								if (sliders.length) {
									const cached = {
										...res,
										international_sliders: sliders,
									};
									sessionStorage.setItem('siteInfo', JSON.stringify(cached));
								}
							} catch {
								// 保持空轮播，不影响页面其余内容
							}
						}
						data.international_sliders = sliders;
						resolve();
						bus.emit('isLoading', false);
					}).catch(() => {
						reject();
					});
				}, 200);
			});
		};

		const internationalPicList = (page = 1, size = 12) => {
			data.picPage = page;
			store.dispatch('common/InternationalList', {
				type: 1,
				page,
				size,
			}).then((res) => {
				const rows = res.data || [];
				if (rows.length > 0) {
					rows.forEach((item) => {
						item.title = item.title.replace(/#/g, '<br>');
					});
					if (page === 1) {
						data.picList = rows;
					} else {
						data.picList = data.picList.concat(rows);
					}
					data.picNomore = rows.length < res.pagination.pageSize;
				} else {
					data.picNomore = true;
				}
				data.picPage += 1;
				data.isPicLoading = false;
				bus.emit('showFooter', true);
			}).catch(() => {
				data.isPicLoading = false;
			});
		};

		const getVideoRatio = (item) => {
			const ratioW = parseFloat(item.ratio_w) || parseFloat(item.thumb_width) || 16;
			const ratioH = parseFloat(item.ratio_h) || parseFloat(item.thumb_height) || 9;
			return { ratioW, ratioH };
		};

		const hasVideoRatio = (item) => {
			const w = parseFloat(item.ratio_w) || parseFloat(item.thumb_width);
			const h = parseFloat(item.ratio_h) || parseFloat(item.thumb_height);
			return w > 0 && h > 0;
		};

		const ensureVideoRatios = async (videoList) => {
			await Promise.all((videoList || []).map(async (item) => {
				if (hasVideoRatio(item) || !item.thumb) return;
				try {
					const size = await getImageSize(item.thumb);
					if (size.width > 0 && size.height > 0) {
						item.ratio_w = size.width;
						item.ratio_h = size.height;
					}
				} catch {
					// 读不到封面尺寸时沿用 16:9
				}
			}));
		};

		const distributeVideos = (videoList) => {
			videoList = videoList || [];
			const isMobileLayout = document.documentElement.clientWidth < 890;
			if (isMobileLayout) {
				data.videoColumns[0].splice(0, data.videoColumns[0].length, ...videoList);
				data.videoColumns[1].splice(0, data.videoColumns[1].length);
				data.videoColumns[2].splice(0, data.videoColumns[2].length);
				return;
			}
			const columns = [[], [], []];
			const columnHeights = [0, 0, 0];
			const width = 423;
			videoList.forEach((item) => {
				const minIndex = columnHeights.indexOf(Math.min(...columnHeights));
				columns[minIndex].push(item);
				const { ratioW, ratioH } = getVideoRatio(item);
				const videoHeight = width * ratioH / ratioW;
				columnHeights[minIndex] += videoHeight + 50;
			});
			data.videoColumns[0].splice(0, data.videoColumns[0].length, ...columns[0]);
			data.videoColumns[1].splice(0, data.videoColumns[1].length, ...columns[1]);
			data.videoColumns[2].splice(0, data.videoColumns[2].length, ...columns[2]);
		};

		const getVideoAspectStyle = (item) => {
			const { ratioW, ratioH } = getVideoRatio(item);
			return {
				objectFit: 'cover',
				width: '100%',
				height: 'auto',
				aspectRatio: `${ratioW} / ${ratioH}`,
				display: 'block',
			};
		};

		const internationalVideoList = (page = 1, size = 12) => {
			data.videoPage = page;
			return store.dispatch('common/InternationalList', {
				type: 2,
				page,
				size,
			}).then(async (res) => {
				const rows = res.data || [];
				if (rows.length > 0) {
					rows.forEach((item) => {
						item.title = item.title.replace(/#/g, '<br>');
					});
					if (page === 1) {
						data.videoList = rows;
					} else {
						data.videoList = data.videoList.concat(rows);
					}
					await ensureVideoRatios(data.videoList);
					distributeVideos(data.videoList);
					data.videoNomore = rows.length < res.pagination.pageSize;
				} else {
					data.videoNomore = true;
				}
				data.videoPage += 1;
				data.isVideoLoading = false;
				bus.emit('showFooter', true);
			}).catch(() => {
				data.isVideoLoading = false;
			});
		};

		const viewBox = ref(null);
		const scroll = (val) => {
			if (!viewBox.value) return;
			viewBox.value.scrollIntoView({
				behavior: 'smooth',
				block: val === 'top' ? 'start' : 'end',
				inline: 'center',
			});
		};

		onMounted(async () => {
			await getSiteInfo();
			internationalPicList(data.picPage);
			data.isVideoLoading = true;
			await internationalVideoList(1);
			setTimeout(() => {
				data.isLoading = false;
				if (route.query.cate_id) {
					const videoSection = document.getElementById('international-video-section');
					if (videoSection) {
						videoSection.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
						});
					}
				}
			}, 600);
		});

		return {
			...toRefs(data),
			viewBox,
			scroll,
			internationalPicList,
			internationalVideoList,
			getVideoAspectStyle,
		};
	},
	mounted() {
		this.$nextTick(() => {
			this.isMobile = document.documentElement.clientWidth < 768;
		});
	},
	methods: {
		loadMore(type) {
			if (type === 1) {
				this.isPicLoading = true;
				this.internationalPicList(this.picPage);
			} else if (type === 2) {
				this.isVideoLoading = true;
				this.internationalVideoList(this.videoPage);
			}
		},
		toVideoDetail(id) {
			const href = this.$router.resolve(buildIntlVideoUrl(id));
			window.open(href.href, '_blank');
		},
		toDetail(id) {
			const href = this.$router.resolve(buildIntlDetailUrl(id));
			window.open(href.href, '_blank');
		},
	},
});
